import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getVendorStats, getAllVendors } from '../../firebaseService';
import { getAuditLogs } from '../../staffPlatformService';
import { AuditLogEntry } from '../../types/staffPlatform';
import { formatAuditAction, formatTimestamp } from '../../staffPlatformService';
import {
  Users,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  ArrowRight,
  Building2,
  ScrollText,
} from 'lucide-react';

interface VendorStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  categories: Record<string, number>;
}

const StaffOverview: React.FC = () => {
  const [stats, setStats] = useState<VendorStats | null>(null);
  const [recentVendors, setRecentVendors] = useState<any[]>([]);
  const [recentLogs, setRecentLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [statsResult, vendorsResult, logsResult] = await Promise.all([
          getVendorStats(),
          getAllVendors(),
          getAuditLogs(8),
        ]);

        if (statsResult.success && 'stats' in statsResult) {
          setStats(statsResult.stats);
        }
        if (vendorsResult.success && vendorsResult.vendors) {
          setRecentVendors(vendorsResult.vendors.slice(0, 5));
        }
        if (logsResult.success && logsResult.logs) {
          setRecentLogs(logsResult.logs);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sunbeth-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
          Monitor vendor applications, approvals, and platform activity at a glance.
        </p>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { label: 'Total Vendors', value: stats.total, icon: Users, color: 'text-sunbeth-orange-600 bg-sunbeth-orange-100' },
            { label: 'Pending Review', value: stats.pending, icon: Clock, color: 'text-yellow-600 bg-yellow-100' },
            { label: 'Approved', value: stats.approved, icon: CheckCircle, color: 'text-green-600 bg-green-100' },
            { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-600 bg-red-100' },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${card.color.split(' ')[1]}`}>
                    <Icon className={`h-6 w-6 ${card.color.split(' ')[0]}`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{card.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-sunbeth-orange-600" />
              Recent Vendor Applications
            </h3>
            <Link to="/staff/dashboard/vendors" className="text-sm text-sunbeth-orange-600 hover:text-sunbeth-orange-700 flex items-center gap-1">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {recentVendors.length === 0 ? (
              <p className="p-5 text-sm text-gray-500">No vendor applications yet.</p>
            ) : (
              recentVendors.map((vendor) => (
                <Link
                  key={vendor.id}
                  to={`/staff/vendor/${vendor.id}`}
                  className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900 truncate text-left">{vendor.companyName}</p>
                    <p className="text-xs text-gray-500 truncate text-left">{vendor.email}</p>
                  </div>
                  <span className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                    vendor.status === 'approved' ? 'bg-green-100 text-green-700' :
                    vendor.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {vendor.status}
                  </span>
                </Link>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-sunbeth-orange-600" />
              Recent Activity
            </h3>
            <Link to="/staff/dashboard/audit-log" className="text-sm text-sunbeth-orange-600 hover:text-sunbeth-orange-700 flex items-center gap-1">
              View log <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {recentLogs.length === 0 ? (
              <p className="p-5 text-sm text-gray-500">No activity recorded yet.</p>
            ) : (
              recentLogs.map((log) => (
                <div key={log.id} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{formatAuditAction(log.action)}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {log.actorName} · {log.targetLabel || log.details || '—'}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{formatTimestamp(log.timestamp)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {stats && Object.keys(stats.categories).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-sunbeth-orange-600" />
            Vendors by Category
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(stats.categories).map(([category, count]) => (
              <div key={category} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-600 capitalize">
                  {category.replace(/([A-Z])/g, ' $1').trim()}
                </p>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffOverview;
